import {FC, useMemo, useState} from "react";
import {App, Flex, Form, FormProps, Modal, Select, SelectProps, Segmented} from "antd";
import {Event, EventAirportPost} from "../../../../types/domain.ts";
import {TimeInput} from "../../../../components/TimeInput/TimeInput.tsx";
import {Dayjs} from "dayjs";
import {useAirports} from "../../../../queries/useAirports.ts";
import {eventsEventIdAirportsPost} from "../../../../api/events/eventsEventIdAirportsPost.ts";
import {isRouteEvent} from "../../../../utils/eventGuards.ts";

type Props = {
    event: Event;
    open: boolean;
    onClose: VoidFunction;
    onSuccess: VoidFunction;
}

type FormType = {
    airport_id: string;
    from: Dayjs;
    to: Dayjs;
    type?: "departure" | "arrival";
}

export const AddAirportModal: FC<Props> = ({ event, open, onClose, onSuccess }) => {
    const { message } = App.useApp();
    const [form] = Form.useForm<FormType>();
    const [loading, setLoading] = useState(false);

    const { data: airports } = useAirports(true);

    const airportsOptions = useMemo(() => {
        const reduced = airports?.reduce<Exclude<SelectProps['options'], undefined>>((acc, { id, name, fir }) => {
            const regionName = fir?.region.name || 'Вне VATRUS';
            const existingRegion = acc.find(({ title }) => title === regionName);

            if (!existingRegion) {
                return [
                    ...acc,
                    {
                        title: regionName,
                        label: regionName,
                        options: [{ label: `${id} ${name} ${fir ? `(РПИ ${fir.name})` : '(Вне VATRUS)'}`, value: id }],
                    },
                ];
            }

            existingRegion.options.push({ label: `${id} ${name} ${fir ? `(РПИ ${fir.name})` : '(Вне VATRUS)'}`, value: id });
            return [...acc];
        }, []);

        if(!reduced) return;

        const withoutVatrus = reduced.filter(g => g.title !== 'Вне VATRUS');
        const vatrusGroup = reduced.find(g => g.title === 'Вне VATRUS');

        return vatrusGroup ? [...withoutVatrus, vatrusGroup] : withoutVatrus;
    }, [airports]);

    const handleFinish: FormProps<FormType>['onFinish'] = async (values) => {
        try {
            setLoading(true);
            const data: EventAirportPost = {
                airport_id: values.airport_id,
                from: values.from.format('HH:mm'),
                to: values.to.format('HH:mm'),
            };
            if (isRouteEvent(event)) {
                data.type = values.type;
            }
            await eventsEventIdAirportsPost(event.id, data);
            void message.success('Аэропорт добавлен');
            onClose();
            onSuccess();
        } catch(e) {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal
            open={open}
            title="Добавить аэропорт"
            onOk={form.submit}
            okButtonProps={{ loading }}
            onCancel={onClose}
            destroyOnClose
        >
            <Form<FormType>
                form={form}
                onFinish={handleFinish}
                layout="vertical"
                preserve={false}
            >
                <Form.Item<FormType>
                    name="airport_id"
                    label="Аэропорт"
                    rules={[{ required: true }]}
                >
                    <Select
                        showSearch
                        options={airportsOptions}
                        optionFilterProp="label"
                        placeholder="Выберите аэропорт"
                    />
                </Form.Item>
                <Flex gap={8}>
                    <Form.Item<FormType>
                        name="from"
                        label="Время от"
                        rules={[{ required: true }]}
                        style={{ flex: 1 }}
                    >
                        <TimeInput placeholder="От" />
                    </Form.Item>
                    <Form.Item<FormType>
                        name="to"
                        label="Время до"
                        dependencies={['from']}
                        rules={[
                            { required: true },
                            ({ getFieldValue }) => ({
                                validator(_, to: Dayjs) {
                                    const from = getFieldValue('from');
                                    if (from && to && from.isAfter(to)) return Promise.reject(new Error('Ошибка'));
                                    return Promise.resolve();
                                }
                            })
                        ]}
                        style={{ flex: 1 }}
                    >
                        <TimeInput placeholder="До" />
                    </Form.Item>
                </Flex>
                {isRouteEvent(event) && (
                    <Form.Item<FormType>
                        name="type"
                        label="Тип"
                        rules={[{ required: true }]}
                    >
                        <Segmented
                            options={[
                                { label: 'Вылет', value: 'departure' },
                                { label: 'Прибытие', value: 'arrival' },
                            ]}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    )
}